import { useCallback, useContext, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { ExperienceTrackerContext } from '@confluence/experience-tracker';
import type {
	END_OF_TRIAL_NUMBER_OF_USERS_EXPERIENCE,
	END_OF_TRIAL_PAGE_INSIGHTS_EXPERIENCE,
	END_OF_TRIAL_PAGE_RESTRICTIONS_EXPERIENCE,
	END_OF_TRIAL_GUEST_ACCESS_EXPERIENCE,
} from '@confluence/experience-tracker';
import { markErrorAsHandled } from '@confluence/graphql-error-processor';

import { useFeatureUsageActions } from '../../store/featureUsageStore';

type Experience =
	| typeof END_OF_TRIAL_PAGE_INSIGHTS_EXPERIENCE
	| typeof END_OF_TRIAL_NUMBER_OF_USERS_EXPERIENCE
	| typeof END_OF_TRIAL_PAGE_RESTRICTIONS_EXPERIENCE
	| typeof END_OF_TRIAL_GUEST_ACCESS_EXPERIENCE;

type ErrorSource =
	| 'useCheckGuestAccess'
	| 'useCheckPageInsights'
	| 'useCheckNumberOfUsers'
	| 'useCheckPageRestrictions';

interface UseEndOfTrialModalAnalyticsParams {
	experienceName?: Experience;
	errorSource: ErrorSource;
}

export const useEndOfTrialModalAnalytics = ({
	experienceName,
	errorSource,
}: UseEndOfTrialModalAnalyticsParams) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { setFeatureUsageError } = useFeatureUsageActions();

	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (experienceName && edition) {
			experienceTracker.start({
				name: experienceName,
				attributes: {
					edition,
				},
			});
		}
	}, [edition, experienceName, experienceTracker]);

	const handleHookError = useCallback(
		(error: Error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'endOfTrialPaymentModalHookError',
					source: 'end-of-trial-payment-modals',
					attributes: {
						edition,
						message: error.message,
						errorSource,
					},
				},
			}).fire();
			markErrorAsHandled(error);

			experienceTracker.fail({
				name: experienceName,
				error,
				attributes: {
					edition,
				},
			});
			void setFeatureUsageError(error);
		},
		[
			createAnalyticsEvent,
			edition,
			errorSource,
			experienceName,
			experienceTracker,
			setFeatureUsageError,
		],
	);

	return {
		handleHookError,
	};
};
